import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { HashLink as Link } from "react-router-hash-link";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CheckoutSummary from "./CheckoutSummary";
import TrialSteps from "./TrialSteps";
import TrialSubmit from "./TrialSubmit";
import { v4 as uuidv4 } from "uuid";
import { createTrialIntentOrder } from "../../actions/orderActions";

function TrialIntent() {
  // Selectors
  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const cartVoucherValid = useSelector((state) => state.cartVoucherValid);
  const { voucherValid } = cartVoucherValid;

  const cartLicenseHolder = useSelector((state) => state.cartLicenseHolder);
  const { licenseHolder } = cartLicenseHolder;

  // Dispatch and Navigate
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State Etc
  const intentUuid = uuidv4();
  const [voucherUse, setVoucherUse] = useState(voucherValid.code);
  const [voucherRate, setVoucherRate] = useState(voucherValid.discount);

  // Cart Totals
  const cartSub = (0).toFixed(2);
  const cartVoucherRate = (0).toFixed(2);
  const cartVoucherTotal = (0).toFixed(2);
  const cartTaxPercent = (0).toFixed(2);
  const cartTaxTotal = (0).toFixed(2);
  const cartTotal = (0).toFixed(2);

  // Cart Totals to Cart
  cart.order_type = "trial";
  cart.subtotal = cartSub;
  cart.vat_rate = cartTaxPercent;
  cart.tax = cartTaxTotal;
  cart.total = cartTotal;

  // useEffects
  useEffect(() => {
    if (!licenseHolder.name) {
      navigate("/checkout/license-holder", { replace: true });
    }
  }, [licenseHolder.name, navigate]);

  useEffect(() => {
    if (voucherUse === "") {
      setVoucherUse("");
      setVoucherRate(voucherRate);
    }
  }, [voucherUse, voucherRate]);

  useEffect(() => {
    dispatch(
      createTrialIntentOrder({
        orderRef: intentUuid,
        orderItems: cart.cartItems,
        billingInfo: licenseHolder,
        licenseHolder: licenseHolder,
        method: "Trial",
        paypalId: "",
        stripeId: "",
        order_type: cart.order_type,
        voucherUse: voucherUse,
        voucherRate: voucherRate,
        voucherAmount: cartVoucherTotal,
        subtotal: cart.subtotal,
        vatRate: cart.vat_rate,
        tax: cart.tax,
        total: cart.total,
      })
    );
  }, [
    dispatch,
    intentUuid,
    cart,
    licenseHolder,
    voucherUse,
    voucherRate,
    cartVoucherTotal,
  ]);

  // Navs and Footer
  useEffect(() => {
    const nav = document.querySelector(".navigation-large");
    nav.className =
      "navigation-large d-none d-md-flex justify-content-between fixed-top section-bg";
  }, []);

  useEffect(() => {
    const nav = document.querySelector(".navigation-small");
    nav.className =
      "navigation-small d-flex justify-content-between d-md-none fixed-top section-bg";
  }, []);

  useEffect(() => {
    const footer = document.querySelector(".footer-contain");
    footer.className = "footer-contain section-bg";
  }, []);

  return (
    <>
      <Container className="section-contain">
        <TrialSteps step1 step2 />
        <Row>
          <Col lg={12}>
            <h1 className="header">Submit Order</h1>
          </Col>
        </Row>
        <Row>
          <Col lg={12} className="header-text d-lg-none">
            <CheckoutSummary />
          </Col>
          <Col lg={3}>
            <Row>
              <Col lg={12}>
                <p className="subhead">License Holder</p>
              </Col>
              <Col lg={12}>{licenseHolder.name}</Col>
              <Col lg={12}>{licenseHolder.company}</Col>
              <Col lg={12}>{licenseHolder.addressOne}</Col>
              <Col lg={12}>{licenseHolder.addressTwo}</Col>
              <Col lg={12}>
                {licenseHolder.city}, {licenseHolder.stateProvince}
              </Col>
              <Col lg={12}>{licenseHolder.zipPostalCode}</Col>
              <Col lg={12}>{licenseHolder.country}</Col>
              <Col lg={12}>{licenseHolder.phone}</Col>
            </Row>
            <Row>
              <Col md={12} className="mt-4 cart-form-link d-lg-none">
                <TrialSubmit cart={cart} licenseHolder={licenseHolder} />
              </Col>
            </Row>
          </Col>
          <Col lg={9} className="d-none d-lg-block">
            <Row>
              <Col lg={12}>
                <p className="subhead">Cart Summary</p>
              </Col>
              <Col md={12}>
                <Container className="payment-section" fluid>
                  {cartItems.map((cartItem, i) => (
                    <Card
                      key={i}
                      className={
                        i === 0
                          ? "cart-card cart-active"
                          : "cart-part-card cart-active"
                      }
                    >
                      <Card.Body>
                        <Row>
                          <Col xs={5} className="my-auto">
                            {cartItem.cartName}
                          </Col>
                          <Col xs={3} className="my-auto">
                            {cartItem.calcName}
                          </Col>
                          <Col xs={4} className="my-auto text-end">
                            $ {cartItem.price.toFixed(2)}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                  {voucherValid && voucherValid.voucher_id !== 0 && (
                    <Card className="cart-part-card">
                      <Card.Body>
                        <Row>
                          <Col xs={8} className="my-auto">
                            Voucher {Math.floor(cartVoucherRate * 100)} %
                          </Col>
                          <Col xs={4} className="my-auto text-end">
                            &minus; $ {cartVoucherTotal}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}
                  <Card className="cart-part-card">
                    <Card.Body>
                      <Row>
                        <Col xs={8} className="my-auto">
                          Subtotal
                        </Col>
                        <Col xs={4} className="my-auto text-end">
                          $ {cartSub}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  <Card className="cart-part-card">
                    <Card.Body>
                      <Row>
                        <Col xs={8} className="my-auto">
                          Tax {cartTaxPercent} %
                        </Col>
                        <Col xs={4} className="my-auto text-end">
                          $ {cartTaxTotal}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  <Card className="cart-part-card">
                    <Card.Body>
                      <Row>
                        <Col xs={8} className="my-auto">
                          Total
                        </Col>
                        <Col xs={4} className="my-auto text-end">
                          $ {cartTotal}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Container>
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mb-4 cart-form-link">
                By clicking the button below, you agree to the{" "}
                <Link to="/eula">GarageFonts EULA</Link>.
              </Col>
              <Col md={4} className="cart-form-link">
                <TrialSubmit intentUuid={intentUuid} />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default TrialIntent;
